import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardBody, 
  Row, 
  Col,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Badge,
  Progress
} from 'reactstrap';
import { Icon } from '../../components/Component';
import Content from '../../layout/content/Content';
import Head from '../../layout/head/Head';
import { Line, Doughnut } from 'react-chartjs-2';
import { defaultUserImage } from '../../utils/envConfig';
import { formatCurrency } from '../../utils/Utils';
import { Chart, ArcElement, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler } from 'chart.js';

// Register Chart.js components
Chart.register(ArcElement, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

// Sample data - would be replaced with API calls in production
const sampleAgentData = [
  {
    "_id": "6578a1b2c3d4e5f6a7b8c9d0",
    "agent": {
      "_id": "6578a1b2c3d4e5f6a7b8c9d1",
      "userId": {
        "_id": "6578a1b2c3d4e5f6a7b8c9d2",
        "first_name": "Mohammed",
        "last_name": "Al-Farsi",
        "email": "mohammed.alfarsi@albacorp.com",
        "phone": "+971501234567",
        "photo": "https://storage.alba.homes/agents/mohammed-alfarsi.jpg",
        "role": "Agent",
        "department": "6578a1b2c3d4e5f6a7b8c9d3"
      },
      "languages": ["English", "Arabic"],
      "RERANo": "12345",
      "agentBio": "Top-performing agent with 10+ years of experience in Dubai luxury real estate."
    },
    "timestamp": "2023-12-01T00:00:00.000Z",
    "day": 1,
    "week": 48,
    "month": 11,
    "quarter": 4,
    "year": 2023,
    "dealsClosedCount": 12,
    "dealsClosedValue": 24500000,
    "listingsAcquiredCount": 15,
    "listingsAcquiredValue": 36500000,
    "leadConversionsCount": 18,
    "activitiesCompletedCount": 65,
    "rankDealsClosedCount": 1,
    "rankDealsClosedValue": 1,
    "rankListingsAcquiredCount": 2,
    "rankListingsAcquiredValue": 2,
    "rankLeadConversionsCount": 2,
    "rankActivitiesCompletedCount": 4,
    "rankOverall": 1,
    "overallScore": 245,
    "periodType": "monthly",
    "departmentId": "6578a1b2c3d4e5f6a7b8c9d3",
    "teamId": "6578a1b2c3d4e5f6a7b8c9d4"
  }
];

// Chart data for performance trends
const performanceTrendData = {
  labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  datasets: [
    {
      label: 'Deals Closed',
      data: [5, 7, 8, 6, 9, 10, 8, 9, 11, 10, 12, 14],
      borderColor: '#4361ee',
      backgroundColor: 'rgba(67, 97, 238, 0.1)',
      tension: 0.4,
      fill: true,
      pointBackgroundColor: '#4361ee',
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
      pointRadius: 4,
      pointHoverRadius: 6,
    },
    {
      label: 'Listings Acquired',
      data: [8, 10, 12, 9, 11, 13, 15, 14, 16, 13, 15, 17],
      borderColor: '#f72585',
      backgroundColor: 'rgba(247, 37, 133, 0.1)',
      tension: 0.4,
      fill: true,
      pointBackgroundColor: '#f72585',
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
      pointRadius: 4,
      pointHoverRadius: 6,
    }
  ]
};

// Chart options
const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top',
      labels: {
        boxWidth: 10,
        usePointStyle: true,
        pointStyle: 'circle'
      }
    },
    tooltip: {
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      padding: 10,
      titleFont: {
        size: 14
      },
      bodyFont: {
        size: 13
      },
      displayColors: false
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      grid: {
        color: 'rgba(219, 223, 234, 0.2)',
        drawBorder: false
      },
      ticks: {
        font: {
          size: 12
        },
        color: '#8094ae',
        padding: 8
      }
    },
    x: {
      grid: {
        display: false,
        drawBorder: false
      },
      ticks: {
        font: {
          size: 12
        },
        color: '#8094ae',
        padding: 5
      }
    }
  }
};

// Doughnut chart data for performance breakdown
const doughnutData = {
  labels: ['Deals Closed', 'Listings Acquired', 'Lead Conversions', 'Activities'],
  datasets: [
    {
      data: [12, 15, 18, 65],
      backgroundColor: ['#4361ee', '#f72585', '#4cc9f0', '#4895ef'],
      borderWidth: 0,
      hoverOffset: 4
    }
  ]
};

const doughnutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: '70%',
  plugins: {
    legend: {
      position: 'bottom',
      labels: {
        boxWidth: 10,
        usePointStyle: true,
        pointStyle: 'circle',
        padding: 20
      }
    },
    tooltip: {
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      padding: 10,
      titleFont: {
        size: 14
      },
      bodyFont: {
        size: 13
      }
    }
  }
};

// Sample tasks data
const sampleTasks = [
  {
    id: 1,
    task: 'Follow-up call with potential buyer',
    client: 'Ahmed Al Mansoori',
    dueDate: 'Today, 2:00 PM',
    priority: 'High',
    status: 'Pending'
  },
  {
    id: 2,
    task: 'Property viewing',
    client: 'Sarah Johnson',
    dueDate: 'Tomorrow, 10:00 AM',
    priority: 'Medium',
    status: 'Pending'
  },
  {
    id: 3,
    task: 'Submit offer documents',
    client: 'Michael Chen',
    dueDate: 'Oct 15, 2023',
    priority: 'High',
    status: 'Pending'
  }
];

// Sample activity data
const sampleActivities = [
  { 
    id: 1,
    type: 'Deal Closed', 
    client: 'Ahmed Al Mansoori', 
    value: 'AED 2,500,000', 
    time: '2 days ago',
    property: null
  },
  { 
    id: 2,
    type: 'Listing Acquired', 
    client: null,
    property: 'Palm Jumeirah Villa', 
    value: 'AED 15,000,000', 
    time: '3 days ago' 
  },
  { 
    id: 3,
    type: 'Lead Converted', 
    client: 'Sarah Johnson', 
    property: null,
    value: null,
    time: '5 days ago' 
  }
];

const ModernAgentDashboard = ({ agent }) => {
  const [activeTab, setActiveTab] = useState('1');
  const [currentAgent, setCurrentAgent] = useState(sampleAgentData[0]);
  const [period, setPeriod] = useState('monthly');

  // Toggle between tabs
  const toggle = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  // In a real implementation, this would fetch data from API
  useEffect(() => {
    // This would be replaced with actual API calls
    // For now, we're using the sample data
    if (agent) {
      setCurrentAgent({
        ...sampleAgentData[0],
        agent: {
          ...sampleAgentData[0].agent,
          userId: {
            ...sampleAgentData[0].agent.userId,
            first_name: agent.user?.first_name || agent.firstName || 'Agent',
            last_name: agent.user?.last_name || agent.lastName || '',
            email: agent.user?.email || agent.email || '',
            phone: agent.user?.phone || agent.phone || '',
            photo: agent.user?.photo || agent.photo || defaultUserImage
          }
        }
      });
    }
  }, [agent, period]);

  const agentInfo = currentAgent.agent;
  const agentUser = agentInfo.userId;

  // Get priority badge color
  const getPriorityBadgeColor = (priority) => {
    switch (priority.toLowerCase()) {
      case 'high': return 'danger';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'secondary';
    }
  };

  // Get status badge color
  const getStatusBadgeColor = (status) => {
    switch (status.toLowerCase()) {
      case 'completed': return 'success';
      case 'in progress': return 'info';
      case 'pending': return 'warning';
      case 'overdue': return 'danger';
      default: return 'secondary';
    }
  };

  // Get activity icon
  const getActivityIcon = (type) => {
    if (type.includes('Deal')) return 'check-circle';
    if (type.includes('Listing')) return 'home';
    if (type.includes('Lead')) return 'user-add';
    if (type.includes('Viewing')) return 'eye';
    return 'activity';
  };

  return (
    <>
      <Head title="Agent Dashboard" />
      <Content>
        <div className="modern-dashboard">
          {/* Header with profile and stats */}
          <div className="dashboard-header mb-4">
            <Row className="g-3 align-items-center">
              <Col lg="8">
                <div className="d-flex align-items-center">
                  <div className="user-avatar xl rounded-circle me-3">
                    <img src={agentUser.photo || defaultUserImage} alt={`${agentUser.first_name} ${agentUser.last_name}`} />
                  </div>
                  <div>
                    <h2 className="mb-1">{`${agentUser.first_name} ${agentUser.last_name}`}</h2>
                    <p className="mb-1 text-muted">{agentInfo.agentBio}</p>
                    <div className="d-flex align-items-center mt-2">
                      <Badge color="primary" pill className="me-2">RERA #{agentInfo.RERANo}</Badge>
                      {agentInfo.languages.map((lang, idx) => (
                        <Badge color="light" pill key={idx} className="me-2">{lang}</Badge>
                      ))}
                      <span className="text-muted ms-2"><Icon name="mail"></Icon> {agentUser.email}</span>
                      <span className="text-muted ms-3"><Icon name="call"></Icon> {agentUser.phone}</span>
                    </div>
                  </div>
                </div>
              </Col>
              <Col lg="4">
                <div className="d-flex justify-content-lg-end mt-3 mt-lg-0">
                  <div className="dropdown">
                    <Button color="primary" className="dropdown-toggle" id="periodDropdown">
                      {period.charAt(0).toUpperCase() + period.slice(1)} Report
                    </Button>
                    <div className="period-selector mt-2">
                      <select 
                        className="form-select" 
                        onChange={(e) => setPeriod(e.target.value)}
                        value={period}
                      >
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="yearly">Yearly</option>
                      </select>
                    </div>
                  </div>
                  <Button color="light" className="ms-2">
                    <Icon name="download"></Icon> Export
                  </Button>
                </div>
              </Col>
            </Row>
          </div>

          {/* Stats Cards */}
          <Row className="g-3 mb-4">
            <Col md="3">
              <Card className="h-100 shadow-sm border-0">
                <CardBody className="p-4">
                  <div className="d-flex align-items-center mb-3">
                    <div className="icon-circle bg-primary-soft me-3">
                      <Icon name="briefcase" className="text-primary"></Icon>
                    </div>
                    <h6 className="mb-0">Deals Closed</h6>
                    <Badge color="primary" pill className="ms-auto">#{currentAgent.rankDealsClosedCount}</Badge>
                  </div>
                  <div className="d-flex align-items-baseline">
                    <h3 className="mb-0">{currentAgent.dealsClosedCount}</h3>
                    <span className="text-success ms-2"><Icon name="arrow-up"></Icon> 12.4%</span>
                  </div>
                  <p className="text-muted mb-0 mt-2">Value: {formatCurrency(currentAgent.dealsClosedValue)}</p>
                </CardBody>
              </Card>
            </Col>
            <Col md="3">
              <Card className="h-100 shadow-sm border-0">
                <CardBody className="p-4">
                  <div className="d-flex align-items-center mb-3">
                    <div className="icon-circle bg-pink-soft me-3">
                      <Icon name="home" className="text-pink"></Icon>
                    </div>
                    <h6 className="mb-0">Listings Acquired</h6>
                    <Badge color="primary" pill className="ms-auto">#{currentAgent.rankListingsAcquiredCount}</Badge>
                  </div>
                  <div className="d-flex align-items-baseline">
                    <h3 className="mb-0">{currentAgent.listingsAcquiredCount}</h3>
                    <span className="text-success ms-2"><Icon name="arrow-up"></Icon> 8.2%</span>
                  </div>
                  <p className="text-muted mb-0 mt-2">Value: {formatCurrency(currentAgent.listingsAcquiredValue)}</p>
                </CardBody>
              </Card>
            </Col>
            <Col md="3">
              <Card className="h-100 shadow-sm border-0">
                <CardBody className="p-4">
                  <div className="d-flex align-items-center mb-3">
                    <div className="icon-circle bg-info-soft me-3">
                      <Icon name="users" className="text-info"></Icon>
                    </div>
                    <h6 className="mb-0">Lead Conversions</h6>
                    <Badge color="primary" pill className="ms-auto">#{currentAgent.rankLeadConversionsCount}</Badge>
                  </div>
                  <div className="d-flex align-items-baseline">
                    <h3 className="mb-0">{currentAgent.leadConversionsCount}</h3>
                    <span className="text-success ms-2"><Icon name="arrow-up"></Icon> 15.7%</span>
                  </div>
                  <p className="text-muted mb-0 mt-2">Conversion Rate: 24.5%</p>
                </CardBody>
              </Card>
            </Col>
            <Col md="3">
              <Card className="h-100 shadow-sm border-0">
                <CardBody className="p-4">
                  <div className="d-flex align-items-center mb-3">
                    <div className="icon-circle bg-success-soft me-3">
                      <Icon name="check-square" className="text-success"></Icon>
                    </div>
                    <h6 className="mb-0">Activities</h6>
                    <Badge color="primary" pill className="ms-auto">#{currentAgent.rankActivitiesCompletedCount}</Badge>
                  </div>
                  <div className="d-flex align-items-baseline">
                    <h3 className="mb-0">{currentAgent.activitiesCompletedCount}</h3>
                    <span className="text-success ms-2"><Icon name="arrow-up"></Icon> 5.2%</span>
                  </div>
                  <p className="text-muted mb-0 mt-2">Completion Rate: 92%</p>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Main Content Tabs */}
          <Nav tabs className="modern-tabs mb-4">
            <NavItem>
              <NavLink
                className={activeTab === '1' ? 'active' : ''}
                onClick={() => toggle('1')}
              >
                <Icon name="bar-chart-2"></Icon> Performance
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === '2' ? 'active' : ''}
                onClick={() => toggle('2')}
              >
                <Icon name="list"></Icon> Tasks
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === '3' ? 'active' : ''}
                onClick={() => toggle('3')}
              >
                <Icon name="activity"></Icon> Activity
              </NavLink>
            </NavItem>
          </Nav>
          
          <TabContent activeTab={activeTab}>
            <TabPane tabId="1">
              <Row className="g-3">
                <Col lg="8">
                  <Card className="shadow-sm border-0 h-100">
                    <CardBody className="p-4">
                      <h5 className="card-title mb-4">Performance Trends</h5>
                      <div style={{ height: '350px' }}>
                        <Line data={performanceTrendData} options={chartOptions} />
                      </div>
                    </CardBody>
                  </Card>
                </Col>
                <Col lg="4">
                  <Card className="shadow-sm border-0 h-100">
                    <CardBody className="p-4">
                      <h5 className="card-title mb-4">Performance Breakdown</h5>
                      <div style={{ height: '250px' }} className="mb-3">
                        <Doughnut data={doughnutData} options={doughnutOptions} />
                      </div>
                      <div className="text-center">
                        <h6>Overall Ranking</h6>
                        <div className="d-inline-block position-relative">
                          <h2 className="display-4 mb-0">{currentAgent.rankOverall}</h2>
                          <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-primary">
                            of 20
                          </span>
                        </div>
                        <p className="text-muted">Performance Score: {currentAgent.overallScore}</p>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </TabPane>
            
            <TabPane tabId="2">
              <Card className="shadow-sm border-0">
                <CardBody className="p-4">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="card-title mb-0">Upcoming Tasks</h5>
                    <Button color="primary" size="sm">
                      <Icon name="plus"></Icon> Add Task
                    </Button>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-borderless table-hover">
                      <thead className="text-muted">
                        <tr>
                          <th>Task</th>
                          <th>Client</th>
                          <th>Due Date</th>
                          <th>Priority</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sampleTasks.map((task) => (
                          <tr key={task.id}>
                            <td>{task.task}</td>
                            <td>{task.client}</td>
                            <td>{task.dueDate}</td>
                            <td>
                              <Badge color={getPriorityBadgeColor(task.priority)} pill>
                                {task.priority}
                              </Badge>
                            </td>
                            <td>
                              <Badge color={getStatusBadgeColor(task.status)} pill>
                                {task.status}
                              </Badge>
                            </td>
                            <td>
                              <Button color="light" size="sm" className="btn-icon">
                                <Icon name="check"></Icon>
                              </Button>
                              <Button color="light" size="sm" className="btn-icon ms-1">
                                <Icon name="edit"></Icon>
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardBody>
              </Card>
            </TabPane>
            
            <TabPane tabId="3">
              <Card className="shadow-sm border-0">
                <CardBody className="p-4">
                  <h5 className="card-title mb-4">Recent Activity</h5>
                  <div className="modern-timeline">
                    {sampleActivities.map((activity) => (
                      <div className="timeline-item" key={activity.id}>
                        <div className="timeline-icon">
                          <Icon name={getActivityIcon(activity.type)}></Icon>
                        </div>
                        <div className="timeline-content">
                          <div className="d-flex justify-content-between">
                            <h6 className="mb-1">{activity.type}</h6>
                            <span className="text-muted small">{activity.time}</span>
                          </div>
                          {activity.client && <p className="mb-1 text-muted"><strong>Client:</strong> {activity.client}</p>}
                          {activity.property && <p className="mb-1 text-muted"><strong>Property:</strong> {activity.property}</p>}
                          {activity.value && <p className="mb-1 text-muted"><strong>Value:</strong> {activity.value}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </TabPane>
          </TabContent>
        </div>
      </Content>
    </>
  );
};

export default ModernAgentDashboard;
